import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  MapPin,
  Pencil,
  X,
  CalendarClock,
  BookOpen,
} from "lucide-react";

import {
  getExamById,
  updateExam,
  updateExamStatus,
  createExamSchedule,
  updateExamSchedule,
  deleteExamSchedule,
  postponeExamSchedule,
  updateExamScheduleStatus,
} from "../../api/examsschedule/examApi";
import {
  getExamByIdPortal,
  updateExamPortal,
  updateExamStatusPortal,
  createExamSchedulePortal,
  updateExamSchedulePortal,
  deleteExamSchedulePortal,
  postponeExamSchedulePortal,
  updateExamScheduleStatusPortal,
} from "../../api/examsschedule/examPortalApi";
import { getAllSubjects } from "../../api/subjects/subjectApi";
import { getAllSubjectsPortal } from "../../api/subjects/subjectPortalApi";
import { getAllClasses } from "../../api/student/studentApi";
import { getAllClassesForPortal } from "../../api/student/studentPortalApi";
import { getAllExamTypes } from "../../api/examsschedule/examTypeApi";
import { getAllExamTypesPortal } from "../../api/examsschedule/examTypePortalApi";
import { useStaffAuth } from "../../context/StaffAuthContext";
import ExamStatusBadge from "../../components/examsschedule/ExamStatusBadge";
import ExamTypeBadge from "../../components/examsschedule/ExamTypeBadge";
import ScheduleStatusBadge from "../../components/examsschedule/ScheduleStatusBadge";
import SyllabusChapterSelect from "../../components/examsschedule/SyllabusChapterSelect";
import PostponeScheduleModal from "../../components/examsschedule/PostponeScheduleModal";
import {
  ALLOWED_STATUS_TRANSITIONS,
  ALLOWED_SCHEDULE_STATUS_TRANSITIONS,
  POSTPONABLE_SCHEDULE_STATUSES,
  LOCKED_SCHEDULE_STATUSES,
  isFullProfile,
} from "../../utils/examsschedule/examConstants";
import {
  formatDate,
  formatDateInput,
  pickList,
  toId,
} from "../../utils/examsschedule/examHelpers";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Alert,
  EmptyState,
  Skeleton,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const emptyScheduleForm = () => ({
  subjectId: "",
  classId: "",
  examDate: "",
  startTime: "",
  endTime: "",
  venue: "",
  maxMarks: "",
  syllabusTemplateId: null,
  chapterNos: [],
});

const ExamDetail = ({ isPortal = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("EXAM_MANAGEMENT_FULL")
    : true;

  const [exam, setExam] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [meta, setMeta] = useState({
    name: "",
    type: "",
    examTypeId: "",
    academicYear: "",
    instructions: "",
    startDate: "",
    endDate: "",
  });

  const toast = useToast();

  const [scheduleForm, setScheduleForm] = useState(emptyScheduleForm());
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [editForm, setEditForm] = useState(emptyScheduleForm());
  const [postponeTarget, setPostponeTarget] = useState(null);

  const basePath = isPortal ? "/staff-panel/exam/exams" : "/exam/exams";
  const seatingPath = isPortal
    ? "/staff-panel/exam/seating"
    : "/exam/seating";

  const flash = (msg, isErr = false) => {
    if (isErr) toast.error(msg);
    else toast.success(msg);
  };

  const loadExam = useCallback(async () => {
    try {
      setLoading(true);
      const res = isPortal ? await getExamByIdPortal(id) : await getExamById(id);
      const ex = res.exam || res.data || res;
      const sch = res.schedules || [];
      setExam(ex);
      setSchedules(Array.isArray(sch) ? sch : pickList(res, ["schedules"]));
      setMeta({
        name: ex.name || "",
        type: ex.type || "",
        examTypeId: toId(ex.examTypeId) || "",
        academicYear: ex.academicYear || "",
        instructions: ex.instructions || "",
        startDate: formatDateInput(ex.startDate),
        endDate: formatDateInput(ex.endDate),
      });
    } catch (err) {
      flash(err.response?.data?.message || "Failed to load exam", true);
    } finally {
      setLoading(false);
    }
  }, [id, isPortal]);

  const loadLookups = useCallback(async () => {
    try {
      const [subRes, classRes, typeRes] = await Promise.all([
        isPortal ? getAllSubjectsPortal() : getAllSubjects(),
        isPortal ? getAllClassesForPortal() : getAllClasses(),
        isPortal
          ? getAllExamTypesPortal({ isActive: "true" })
          : getAllExamTypes({ isActive: "true" }),
      ]);
      setSubjects(pickList(subRes, ["subjects", "data"]));
      setClasses(pickList(classRes, ["classes", "data"]));
      setExamTypes(pickList(typeRes, ["types", "data"]));
    } catch (e) {
      console.error(e);
    }
  }, [isPortal]);

  useEffect(() => {
    loadExam();
    loadLookups();
  }, [loadExam, loadLookups]);

  const lockedExam = ["COMPLETED", "RESULTS_PUBLISHED"].includes(
    exam?.status
  );

  const defaultMarksForExam = () => {
    const t =
      examTypes.find((x) => x.code === exam?.type) ||
      examTypes.find((x) => toId(x) === toId(exam?.examTypeId));
    return t?.defaultFullMarks > 0 ? String(t.defaultFullMarks) : "";
  };

  const handleStatus = async (newStatus) => {
    try {
      setSubmitting(true);
      if (isPortal) await updateExamStatusPortal(id, newStatus);
      else await updateExamStatus(id, newStatus);
      flash(`Status → ${newStatus}`);
      await loadExam();
    } catch (err) {
      flash(err.response?.data?.message || "Status update failed", true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveMeta = async () => {
    if (!meta.name?.trim() || !meta.type) {
      flash("Name and exam type are required", true);
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        name: meta.name.trim(),
        type: meta.type,
        examTypeId: meta.examTypeId || undefined,
        instructions: meta.instructions,
        startDate: meta.startDate || null,
        endDate: meta.endDate || null,
      };
      if (isPortal) await updateExamPortal(id, payload);
      else await updateExam(id, payload);
      flash("Exam updated");
      setEditingScheduleId(null);
      await loadExam();
    } catch (err) {
      flash(err.response?.data?.message || "Update failed", true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    if (
      !scheduleForm.subjectId ||
      !scheduleForm.classId ||
      !scheduleForm.examDate ||
      !scheduleForm.maxMarks
    ) {
      flash("Subject, class, date and full marks are required", true);
      return;
    }
    if (!scheduleForm.syllabusTemplateId) {
      flash("Syllabus template is required", true);
      return;
    }
    if (
      !Array.isArray(scheduleForm.chapterNos) ||
      scheduleForm.chapterNos.length === 0
    ) {
      flash("Select at least one syllabus chapter", true);
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        examId: id,
        subjectId: scheduleForm.subjectId,
        classId: scheduleForm.classId,
        examDate: scheduleForm.examDate,
        startTime: scheduleForm.startTime || "",
        endTime: scheduleForm.endTime || "",
        venue: scheduleForm.venue || "",
        maxMarks: Number(scheduleForm.maxMarks),
        syllabusTemplateId: scheduleForm.syllabusTemplateId,
        chapterNos: scheduleForm.chapterNos || [],
      };
      if (isPortal) await createExamSchedulePortal(payload);
      else await createExamSchedule(payload);
      flash("Schedule added");
      setScheduleForm({
        ...emptyScheduleForm(),
        maxMarks: defaultMarksForExam(),
      });
      await loadExam();
    } catch (err) {
      flash(err.response?.data?.message || "Failed to add schedule", true);
    } finally {
      setSubmitting(false);
    }
  };

  const startEditSchedule = (s) => {
    if (LOCKED_SCHEDULE_STATUSES.includes(s.scheduleStatus)) {
      flash("Cannot edit a completed or cancelled schedule", true);
      return;
    }
    setEditingScheduleId(toId(s));
    setEditForm({
      subjectId: toId(s.subjectId),
      classId: toId(s.classId),
      examDate: formatDateInput(s.examDate),
      startTime: s.startTime || "",
      endTime: s.endTime || "",
      venue: s.venue || "",
      maxMarks: s.maxMarks != null ? String(s.maxMarks) : "",
      syllabusTemplateId: toId(s.syllabusTemplateId) || null,
      chapterNos: Array.isArray(s.chapterNos) ? [...s.chapterNos] : [],
    });
  };

  const cancelEditSchedule = () => {
    setEditingScheduleId(null);
    setEditForm(emptyScheduleForm());
  };

  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    if (!editingScheduleId) return;
    if (!editForm.maxMarks) {
      flash("Full marks is required", true);
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        startTime: editForm.startTime || "",
        endTime: editForm.endTime || "",
        venue: editForm.venue || "",
        maxMarks: Number(editForm.maxMarks),
        syllabusTemplateId: editForm.syllabusTemplateId || null,
        chapterNos: editForm.chapterNos || [],
      };
      if (isPortal)
        await updateExamSchedulePortal(editingScheduleId, payload);
      else await updateExamSchedule(editingScheduleId, payload);
      flash("Schedule updated");
      cancelEditSchedule();
      await loadExam();
    } catch (err) {
      flash(err.response?.data?.message || "Schedule update failed", true);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePostpone = async ({ newDate, reason }) => {
    if (!postponeTarget) return;
    try {
      setSubmitting(true);
      const sid = toId(postponeTarget);
      if (isPortal) {
        await postponeExamSchedulePortal(sid, { newDate, reason });
      } else {
        await postponeExamSchedule(sid, { newDate, reason });
      }
      flash("Schedule postponed");
      setPostponeTarget(null);
      await loadExam();
    } catch (err) {
      flash(err.response?.data?.message || "Postpone failed", true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleStatus = async (scheduleId, status) => {
    try {
      setSubmitting(true);
      if (isPortal) {
        await updateExamScheduleStatusPortal(scheduleId, status);
      } else {
        await updateExamScheduleStatus(scheduleId, status);
      }
      flash(`Schedule → ${status.replace(/_/g, " ")}`);
      await loadExam();
    } catch (err) {
      flash(err.response?.data?.message || "Status update failed", true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm("Delete this schedule?")) return;
    try {
      if (isPortal) await deleteExamSchedulePortal(scheduleId);
      else await deleteExamSchedule(scheduleId);
      flash("Schedule deleted");
      if (editingScheduleId === scheduleId) cancelEditSchedule();
      await loadExam();
    } catch (err) {
      flash(err.response?.data?.message || "Delete failed", true);
    }
  };

  const handleMetaTypeChange = (code) => {
    const t = examTypes.find((x) => x.code === code);
    setMeta({
      ...meta,
      type: code,
      examTypeId: toId(t) || "",
    });
  };

  useEffect(() => {
    if (!exam || !examTypes.length) return;
    setScheduleForm((prev) => {
      if (prev.maxMarks) return prev;
      const def = defaultMarksForExam();
      return def ? { ...prev, maxMarks: def } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam, examTypes]);

  const examTypeOptions = [
    ...(examTypes.length === 0
      ? [{ label: meta.type || "—", value: meta.type }]
      : []),
    ...examTypes.map((t) => ({
      label: `${t.name} — ${t.scheduleProfile}`,
      value: t.code,
    })),
  ];

  const classOptions = [
    { label: "Select Class *", value: "" },
    ...classes.map((c) => ({
      label: c.className || c.name,
      value: toId(c),
    })),
  ];

  const subjectOptions = [
    { label: "Select Subject *", value: "" },
    ...subjects.map((s) => ({
      label: s.name,
      value: toId(s),
    })),
  ];

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6 p-6">
        <Skeleton height={24} width="150px" />
        <Skeleton height={120} width="100%" />
        <Skeleton height={300} width="100%" />
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="mx-auto max-w-6xl p-6 text-center">
        <Alert variant="danger" title="Error">Exam not found</Alert>
      </div>
    );
  }

  const nextStatuses = ALLOWED_STATUS_TRANSITIONS[exam.status] || [];
  const full = isFullProfile(exam);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Back Button */}
      <button
        type="button"
        onClick={() => navigate(basePath)}
        className="inline-flex items-center gap-1 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to exams
      </button>

      {/* Alerts */}
      
      

      {/* Header & Exam Metadata Card */}
      <Card>
        <CardHeader className="flex flex-col gap-4 border-b bg-neutral-50/50 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {exam.name}
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <ExamTypeBadge type={exam.type} />
              <ExamStatusBadge status={exam.status} />
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                {exam.academicYear}
                {full ? " · Full seating profile" : " · Light schedule"}
              </span>
            </div>
          </div>

          {canManage && nextStatuses.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {nextStatuses.map((s) => (
                <Button
                  key={s}
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  disabled={submitting}
                  onClick={() => handleStatus(s)}
                >
                  → {s.replace(/_/g, " ")}
                </Button>
              ))}
            </div>
          )}
        </CardHeader>

        {canManage && exam.status !== "RESULTS_PUBLISHED" && (
          <CardContent className="space-y-4 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <Input
                label="Name *"
                value={meta.name}
                onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                disabled={lockedExam}
              />
              <Select
                label="Exam Type * (from master)"
                options={examTypeOptions}
                value={meta.type}
                disabled={lockedExam}
                onChange={(val) => handleMetaTypeChange(val)}
              />
              <div>
                <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Academic Year (read-only)
                </label>
                <div className="border border-neutral-200 dark:border-neutral-800 rounded px-3 py-2 text-sm bg-neutral-50 dark:bg-neutral-950 text-neutral-600 dark:text-neutral-300">
                  {meta.academicYear || "—"}
                </div>
              </div>
              <Input
                label="Instructions"
                value={meta.instructions}
                onChange={(e) =>
                  setMeta({ ...meta, instructions: e.target.value })
                }
                disabled={lockedExam}
              />
              <Input
                type="date"
                label="Start Date"
                value={meta.startDate}
                onChange={(e) =>
                  setMeta({ ...meta, startDate: e.target.value })
                }
                disabled={lockedExam}
              />
              <Input
                type="date"
                label="End Date"
                value={meta.endDate}
                onChange={(e) =>
                  setMeta({ ...meta, endDate: e.target.value })
                }
                disabled={lockedExam}
              />
            </div>
            {!lockedExam && (
              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSaveMeta}
                  isLoading={submitting}
                >
                  Save Exam Details
                </Button>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Add Schedule Form */}
      {canManage && !lockedExam && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary-900 dark:text-primary-300 shrink-0" />
              Add Schedule
            </CardTitle>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              Academic year is taken from the exam. Class and subject are selects. Syllabus is filtered by class + subject. Date changes after create use Postpone.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddSchedule} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <Select
                  options={classOptions}
                  value={scheduleForm.classId}
                  onChange={(val) =>
                    setScheduleForm({
                      ...scheduleForm,
                      classId: val,
                      syllabusTemplateId: null,
                      chapterNos: [],
                    })
                  }
                />
                <Select
                  options={subjectOptions}
                  value={scheduleForm.subjectId}
                  onChange={(val) =>
                    setScheduleForm({
                      ...scheduleForm,
                      subjectId: val,
                      syllabusTemplateId: null,
                      chapterNos: [],
                    })
                  }
                />
                <Input
                  type="date"
                  value={scheduleForm.examDate}
                  onChange={(e) =>
                    setScheduleForm({
                      ...scheduleForm,
                      examDate: e.target.value,
                    })
                  }
                  required
                />
                <Input
                  type="number"
                  min={1}
                  placeholder="Full marks *"
                  value={scheduleForm.maxMarks}
                  onChange={(e) =>
                    setScheduleForm({
                      ...scheduleForm,
                      maxMarks: e.target.value,
                    })
                  }
                  required
                />
                <Input
                  type="time"
                  label="Start Time"
                  value={scheduleForm.startTime}
                  onChange={(e) =>
                    setScheduleForm({
                      ...scheduleForm,
                      startTime: e.target.value,
                    })
                  }
                />
                <Input
                  type="time"
                  label="End Time"
                  value={scheduleForm.endTime}
                  onChange={(e) =>
                    setScheduleForm({
                      ...scheduleForm,
                      endTime: e.target.value,
                    })
                  }
                />
                {!full && (
                  <div className="sm:col-span-2 md:col-span-3">
                    <Input
                      placeholder="Venue (optional for light profile)"
                      value={scheduleForm.venue}
                      onChange={(e) =>
                        setScheduleForm({ ...scheduleForm, venue: e.target.value })
                      }
                    />
                  </div>
                )}
              </div>

              <SyllabusChapterSelect
                isPortal={isPortal}
                classId={scheduleForm.classId}
                subjectId={scheduleForm.subjectId}
                academicYear={exam?.academicYear}
                syllabusTemplateId={scheduleForm.syllabusTemplateId}
                chapterNos={scheduleForm.chapterNos}
                required
                onChange={(v) =>
                  setScheduleForm({
                    ...scheduleForm,
                    syllabusTemplateId: v.syllabusTemplateId,
                    chapterNos: v.chapterNos,
                  })
                }
              />

              <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
                {full
                  ? "FULL profile: after adding a schedule, open Seating for invigilators + student bench grid."
                  : "LIGHT profile: venue only (no seating plan / invigilators)."}
              </p>

              <Button
                type="submit"
                variant="primary"
                isLoading={submitting}
              >
                Add Schedule
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Schedules List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary-900 dark:text-primary-300 shrink-0" />
            Schedules ({schedules.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {schedules.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={BookOpen}
                title="No schedules yet"
                description="Add a schedule above to specify class, subject, and time slots."
              />
            </div>
          ) : (
            <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {schedules.map((s) => {
                const sid = toId(s);
                const isEditing = editingScheduleId === sid;
                const locked = LOCKED_SCHEDULE_STATUSES.includes(
                  s.scheduleStatus
                );
                const canPostpone = POSTPONABLE_SCHEDULE_STATUSES.includes(
                  s.scheduleStatus
                );
                const nextSchedStatuses =
                  ALLOWED_SCHEDULE_STATUS_TRANSITIONS[s.scheduleStatus] || [];

                return (
                  <li key={sid} className="p-4 space-y-3 hover:bg-neutral-50/50 transition-colors">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-neutral-900 dark:text-neutral-100">
                          {s.subjectId?.name || "Subject"} ·{" "}
                          {s.classId?.className || "Class"}
                        </div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 flex flex-wrap gap-2 items-center">
                          <span>{formatDate(s.examDate)}</span>
                          {(s.startTime || s.endTime) && (
                            <span>
                              {s.startTime}
                              {s.endTime ? `–${s.endTime}` : ""}
                            </span>
                          )}
                          <span>Max {s.maxMarks}</span>
                          <ScheduleStatusBadge status={s.scheduleStatus} />
                          {s.syllabusTemplateId?.title && (
                            <span className="text-primary-900 dark:text-primary-300 font-medium">
                              {s.syllabusTemplateId.title}
                              {s.chapterNos?.length
                                ? ` (ch ${s.chapterNos.join(", ")})`
                                : ""}
                            </span>
                          )}
                          {s.venue && (
                            <span className="text-neutral-400 dark:text-neutral-500">{s.venue}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {full && canManage && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => navigate(`${seatingPath}/${sid}`)}
                            leftIcon={<MapPin className="h-3.5 w-3.5" />}
                            className="text-accent-600 hover:bg-accent-50"
                          >
                            Seating
                          </Button>
                        )}
                        {canManage && !lockedExam && canPostpone && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => setPostponeTarget(s)}
                            leftIcon={<CalendarClock className="h-3.5 w-3.5" />}
                            className="text-warning-700 hover:bg-warning-50"
                          >
                            Postpone
                          </Button>
                        )}
                        {canManage && !lockedExam && !isEditing && !locked && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => startEditSchedule(s)}
                            className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canManage && !lockedExam && !locked && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => handleDeleteSchedule(sid)}
                            className="text-danger-500 hover:text-danger-700 hover:bg-danger-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Schedule Status Transitions */}
                    {canManage && !lockedExam && nextSchedStatuses.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {nextSchedStatuses.map((st) => (
                          <Button
                            key={st}
                            type="button"
                            variant="secondary"
                            size="xs"
                            disabled={submitting}
                            onClick={() => handleScheduleStatus(sid, st)}
                          >
                            → {st.replace(/_/g, " ")}
                          </Button>
                        ))}
                      </div>
                    )}

                    {/* Schedule Inline Edit Form */}
                    {isEditing && (
                      <form
                        onSubmit={handleUpdateSchedule}
                        className="bg-neutral-50 dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 space-y-3 mt-2"
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wide">
                            Edit Schedule
                          </h3>
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={cancelEditSchedule}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                          Subject, class and exam date are fixed here. To change the date, use Postpone. Editable: time, marks, venue, syllabus.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                              Exam date (use Postpone)
                            </label>
                            <div className="border border-neutral-200 dark:border-neutral-800 rounded px-3 py-2 text-sm bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
                              {formatDate(s.examDate)}
                            </div>
                          </div>
                          <Input
                            type="number"
                            min={1}
                            label="Full Marks *"
                            value={editForm.maxMarks}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                maxMarks: e.target.value,
                              })
                            }
                            required
                          />
                          <Input
                            type="time"
                            label="Start Time"
                            value={editForm.startTime}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                startTime: e.target.value,
                              })
                            }
                          />
                          <Input
                            type="time"
                            label="End Time"
                            value={editForm.endTime}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                endTime: e.target.value,
                              })
                            }
                          />
                          {!full && (
                            <Input
                              label="Venue"
                              value={editForm.venue}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  venue: e.target.value,
                                })
                              }
                            />
                          )}
                        </div>

                        <SyllabusChapterSelect
                          isPortal={isPortal}
                          classId={editForm.classId}
                          subjectId={editForm.subjectId}
                          academicYear={exam.academicYear}
                          syllabusTemplateId={editForm.syllabusTemplateId}
                          chapterNos={editForm.chapterNos}
                          onChange={(v) =>
                            setEditForm({
                              ...editForm,
                              syllabusTemplateId: v.syllabusTemplateId,
                              chapterNos: v.chapterNos,
                            })
                          }
                        />

                        <div className="flex gap-2 pt-2">
                          <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            isLoading={submitting}
                          >
                            Save Schedule
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={cancelEditSchedule}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {postponeTarget && (
        <PostponeScheduleModal
          schedule={postponeTarget}
          submitting={submitting}
          onClose={() => setPostponeTarget(null)}
          onSubmit={handlePostpone}
        />
      )}
    </div>
  );
};

export default ExamDetail;